// App-layer AEAD for the data channel. LiveKit's E2EE worker encrypts MEDIA
// frames only — data packets (chat, polls, reactions, file refs, …) cross the
// SFU under transport crypto alone, which is not end-to-end. In encrypted
// rooms every data payload is therefore sealed here before publishData and
// opened after DataReceived, so the SFU relays opaque bytes for the whole
// data plane, not just media.
//
// Key: HKDF-SHA256(outerDerived, salt = sessionId, info = "bz-data-v1") →
// AES-256-GCM, where outerDerived is the §5.2 outer HKDF output — the same
// material the media worker and per-file keys already build on. One key per
// session, shared by all admitted peers (v1 shared-key model, §5.5).
//
// Envelope: [0x02][12-byte nonce][GCM ciphertext‖tag], AAD = the topic
// string. Binding the topic means a ciphertext captured on one topic cannot
// be replayed onto another (e.g. a private message re-injected as room chat).
// Version 0x02 can never collide with the legacy plaintext frames, which are
// JSON and start with '{' (0x7B).
//
// iOS derives and seals identically (ChatCrypto.swift); the two are pinned to
// shared vectors generated from an independent implementation.

export const DATA_HKDF_INFO = 'bz-data-v1';
export const SEALED_VERSION = 0x02;
const NONCE_LENGTH = 12;

const enc = new TextEncoder();

/** Derive the session data-plane key from the §5.2 outer HKDF output. */
export async function deriveDataKey(
  outerDerived: Uint8Array<ArrayBuffer>,
  sessionId: string,
): Promise<CryptoKey> {
  const ikm = await crypto.subtle.importKey('raw', outerDerived, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: enc.encode(sessionId), info: enc.encode(DATA_HKDF_INFO) },
    ikm,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Seal a data-channel payload. `nonce` is injectable for the vector tests
 * only — production always draws a fresh random nonce. */
export async function seal(
  key: CryptoKey,
  topic: string,
  plaintext: Uint8Array<ArrayBuffer>,
  nonce?: Uint8Array<ArrayBuffer>,
): Promise<Uint8Array<ArrayBuffer>> {
  const iv = nonce ?? crypto.getRandomValues(new Uint8Array(new ArrayBuffer(NONCE_LENGTH)));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: enc.encode(topic) },
    key,
    plaintext,
  );
  const out = new Uint8Array(new ArrayBuffer(1 + NONCE_LENGTH + ct.byteLength));
  out[0] = SEALED_VERSION;
  out.set(iv, 1);
  out.set(new Uint8Array(ct), 1 + NONCE_LENGTH);
  return out;
}

/** Open a sealed payload. Returns null for anything that is not an authentic
 * sealed frame for this topic — wrong version (including legacy plaintext),
 * truncation, tampering, or a cross-topic replay. Callers drop nulls. */
export async function open(
  key: CryptoKey,
  topic: string,
  payload: Uint8Array,
): Promise<Uint8Array | null> {
  if (payload.length < 1 + NONCE_LENGTH + 16 || payload[0] !== SEALED_VERSION) return null;
  const iv = payload.slice(1, 1 + NONCE_LENGTH);
  const ct = payload.slice(1 + NONCE_LENGTH);
  try {
    const pt = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv, additionalData: enc.encode(topic) },
      key,
      ct,
    );
    return new Uint8Array(pt);
  } catch {
    return null;
  }
}
